import clsx from 'clsx';
import { type FC, type PropsWithChildren } from 'react';
import { Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import './SidebarButton.scss';

type Props = PropsWithChildren<{
  label: string
  link?: string
  onClick?: () => void
  sidebarOpen: boolean
}>;

const SidebarButton: FC<Props> = ({ children, label, link, onClick, sidebarOpen }) => {
  const buttonClasses = clsx(
    'sidebar-button',
    'relative w-full flex items-center gap-4 px-4 py-3 my-1 rounded-xl transition-all duration-300 group overflow-hidden',
    'hover:bg-indigo-50 dark:hover:bg-white/5',
    'text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400'
  );

  const content = (
    <>
      <div className="min-w-[20px] flex items-center justify-center transition-colors duration-300 group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
        {children}
      </div>
      <p className={clsx(
        'whitespace-nowrap font-medium transition-all duration-300 origin-left',
        sidebarOpen ? 'w-auto opacity-100 translate-x-0' : 'w-0 opacity-0 -translate-x-4 overflow-hidden'
      )}>
        {label}
      </p>

      {/* Active Indicator (Optional - logic can be added later based on current route) */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-indigo-600 rounded-r-full opacity-0 transition-opacity duration-300" />
    </>
  );

  // getTooltipContainer: renderizamos el tooltip en document.body para
  // escapar el `overflow-x-hidden` del contenedor de navegación cuando
  // la sidebar está colapsada. Sin esto, el tooltip se clipea al
  // intentar salir por la derecha y antd termina recolocándolo
  // verticalmente (debajo del botón en lugar de al lado).
  const tooltipContent = !sidebarOpen ? (
    <span className="sidebar-button__tooltip">{label}</span>
  ) : (
    <span />
  );

  if (link) {
    return (
      <Tooltip
        placement="right"
        title={tooltipContent}
        mouseEnterDelay={0.05}
        mouseLeaveDelay={0.1}
        getTooltipContainer={() => document.body}
        overlayClassName="sidebar-button-tooltip"
      >
        <Link to={link} className="block w-full">
          <button className={buttonClasses}>
            {content}
          </button>
        </Link>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      placement="right"
      title={tooltipContent}
      mouseEnterDelay={0.05}
      mouseLeaveDelay={0.1}
      getTooltipContainer={() => document.body}
      overlayClassName="sidebar-button-tooltip"
    >
      <button className={buttonClasses} onClick={onClick}>
        {content}
      </button>
    </Tooltip>
  );
};

export { SidebarButton };